/* ZYMA slider — <zyma-slider>, shared by the hero and "Meet the range".
   Markup contract (all optional except track + slides):
     [data-slider-track]      scroll container holding the slides
     [data-slider-slide]      one per slide; data-fg-rgb / data-bg-rgb feed --slider-fg-rgb / --slider-bg-rgb
     [data-slider-dot="i"]    any number of switchers; every button whose value is i is marked aria-current
     [data-slider-scroller]   a scrolling switcher row; the current button is scrolled into view
     [data-slider-prev|next|toggle]  buttons; toggle uses data-label-pause / data-label-play
     [data-slider-current]    text updated to the 1-based, zero-padded slide number
     [data-slider-controls]   area that never starts a swipe
   Attributes: data-transition="slide|fade", data-autoplay="true", data-speed (ms).
   Slide transition uses native scroll-snap (touch momentum for free); fade uses pointer swipes.
   Custom element lifecycle covers shopify:section:load/unload; block select/deselect is handled here. */
(() => {
  'use strict';

  if (customElements.get('zyma-slider')) return;

  const SWIPE_THRESHOLD = 40;
  const DRAG_START = 6;

  class ZymaSlider extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slider-slide]')];
      this.track = this.querySelector('[data-slider-track]');
      if (this.slides.length < 2 || !this.track) return;

      this.controller = new AbortController();
      const { signal } = this.controller;

      this.dots = [...this.querySelectorAll('[data-slider-dot]')];
      this.counters = [...this.querySelectorAll('[data-slider-current]')];
      this.toggle = this.querySelector('[data-slider-toggle]');
      this.transition = this.dataset.transition === 'fade' ? 'fade' : 'slide';
      this.speed = Math.max(3000, Number(this.dataset.speed) || 5000);
      this.autoplay = this.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.reducedMotion.matches;
      this.holds = new Set();
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.pending = null;
      this.timer = 0;

      this.querySelectorAll('[data-slider-prev]').forEach((button) => button.addEventListener('click', () => this.go(this.index - 1), { signal }));
      this.querySelectorAll('[data-slider-next]').forEach((button) => button.addEventListener('click', () => this.go(this.index + 1), { signal }));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.sliderDot) || 0), { signal }));
      this.toggle?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.sync();
      }, { signal });

      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.hold('hover', true);
      }, { signal });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.hold('hover', false);
      }, { signal });
      this.addEventListener('focusin', (event) => {
        if (event.target instanceof Element && event.target.matches(':focus-visible')) this.hold('focus', true);
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hold('focus', false);
      }, { signal });
      document.addEventListener('visibilitychange', () => this.hold('hidden', document.hidden), { signal });

      if (this.transition === 'slide') {
        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
        this.track.addEventListener('scrollend', () => this.settle(), { signal });
        this.bindMouseDrag(signal);
      } else {
        this.bindSwipe(signal);
      }

      // Theme editor: selecting a slide block jumps to it and holds autoplay until deselected.
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target);
        if (index < 0) return;
        this.hold('editor', true);
        this.go(index, { instant: true });
      }, { signal });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (this.slides.includes(event.target)) this.hold('editor', false);
      }, { signal });

      this.render();
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      window.clearTimeout(this.timer);
      window.clearTimeout(this.pendingTimer);
    }

    get isRtl() {
      return getComputedStyle(this).direction === 'rtl';
    }

    get playing() {
      return this.autoplay && !this.userPaused && this.holds.size === 0;
    }

    go(target, { instant = false } = {}) {
      const count = this.slides.length;
      const next = ((target % count) + count) % count;

      if (this.transition === 'slide') {
        const left = this.slides[next].offsetLeft - this.slides[0].offsetLeft;
        this.pending = next;
        window.clearTimeout(this.pendingTimer);
        this.pendingTimer = window.setTimeout(() => { this.pending = null; }, 1200);
        this.track.scrollTo({ left, behavior: instant || this.reducedMotion.matches ? 'instant' : 'smooth' });
      }

      this.index = next;
      this.render();
      this.restart();
    }

    currentScrollIndex() {
      const width = this.track.clientWidth || 1;
      return Math.min(this.slides.length - 1, Math.max(0, Math.round(Math.abs(this.track.scrollLeft) / width)));
    }

    onScroll() {
      const index = this.currentScrollIndex();
      if (this.pending !== null) {
        if (index === this.pending) this.pending = null;
        return;
      }
      if (index !== this.index) {
        this.index = index;
        this.render();
        this.restart();
      }
    }

    settle() {
      this.pending = null;
      this.onScroll();
    }

    render() {
      const focusedSlide = this.slides.find((slide) => slide.contains(document.activeElement));
      const active = this.slides[this.index];

      this.slides.forEach((slide, index) => {
        const current = index === this.index;
        slide.classList.toggle('is-active', current);
        slide.toggleAttribute('inert', !current);
      });

      this.dots.forEach((dot) => {
        if (Number(dot.dataset.sliderDot) !== this.index) {
          dot.removeAttribute('aria-current');
          return;
        }
        dot.setAttribute('aria-current', 'true');
        this.revealInScroller(dot);
      });

      const label = String(this.index + 1).padStart(2, '0');
      this.counters.forEach((counter) => { counter.textContent = label; });

      // How far through the set we are, 0-1. A progress bar can then be pure CSS off this one value,
      // which is what the mobile gallery uses instead of dots.
      this.style.setProperty('--slider-progress', String((this.index + 1) / this.slides.length));

      if (active.dataset.fgRgb) this.style.setProperty('--slider-fg-rgb', active.dataset.fgRgb);
      if (active.dataset.bgRgb) this.style.setProperty('--slider-bg-rgb', active.dataset.bgRgb);

      // Keep keyboard focus inside the carousel when its slide goes inert.
      if (focusedSlide && focusedSlide !== active) {
        const target = active.querySelector('a, button') || this.dots.find((dot) => dot.hasAttribute('aria-current')) || this;
        target.focus({ preventScroll: true });
      }

      this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { index: this.index } }));
    }

    revealInScroller(dot) {
      const scroller = dot.closest('[data-slider-scroller]');
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth + 1) return;
      const left = dot.offsetLeft - (scroller.clientWidth - dot.offsetWidth) / 2;
      scroller.scrollTo({ left, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    }

    hold(reason, on) {
      if (on) this.holds.add(reason);
      else this.holds.delete(reason);
      this.sync();
    }

    sync() {
      this.track.setAttribute('aria-live', this.playing ? 'off' : 'polite');
      if (this.toggle) {
        this.toggle.classList.toggle('is-paused', this.userPaused);
        this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
      }
      this.restart();
    }

    restart() {
      window.clearTimeout(this.timer);
      this.classList.remove('is-playing');
      if (!this.playing) return;
      void this.offsetWidth; // restart indicator progress animations
      this.classList.add('is-playing');
      this.timer = window.setTimeout(() => this.go(this.index + 1), this.speed);
    }

    onKeydown(event) {
      if (event.target instanceof Element && event.target.closest('input, select, textarea')) return;
      const forward = this.isRtl ? -1 : 1;
      const moves = { ArrowRight: forward, ArrowLeft: -forward };
      let target = null;
      if (event.key in moves) target = this.index + moves[event.key];
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = this.slides.length - 1;
      if (target === null) return;

      event.preventDefault();
      this.go(target);
      // Keyboard users on a switcher follow the current slide's button.
      if (event.target instanceof Element && event.target.matches('[data-slider-dot]')) {
        this.dots.find((dot) => dot.hasAttribute('aria-current') && dot.offsetParent)?.focus({ preventScroll: true });
      }
    }

    suppressNextClick(signal) {
      this.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true, once: true, signal });
    }

    // Slide transition: mouse users can drag the track like a touch swipe.
    bindMouseDrag(signal) {
      let drag = null;

      this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft, index: this.index, moved: false };
      }, { signal });

      this.track.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x;
        if (!drag.moved) {
          if (Math.abs(dx) < DRAG_START) return;
          drag.moved = true;
          this.track.setPointerCapture(event.pointerId);
          this.classList.add('is-dragging');
        }
        this.track.scrollLeft = drag.left - dx;
      }, { signal });

      const end = (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const { moved, x, index } = drag;
        drag = null;
        if (!moved) return;
        this.suppressNextClick(signal);
        this.classList.remove('is-dragging');
        const dx = event.clientX - x;
        const direction = dx < 0 ? 1 : -1;
        const step = Math.abs(dx) > this.track.clientWidth * .12 ? direction * (this.isRtl ? -1 : 1) : 0;
        this.go(Math.min(this.slides.length - 1, Math.max(0, index + step)));
      };
      this.track.addEventListener('pointerup', end, { signal });
      this.track.addEventListener('pointercancel', end, { signal });
    }

    // Fade transition: horizontal swipe (touch, pen or mouse) changes slide.
    bindSwipe(signal) {
      let start = null;

      this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || (event.target instanceof Element && event.target.closest('[data-slider-controls]'))) return;
        start = { x: event.clientX, y: event.clientY };
      }, { signal });

      this.addEventListener('pointerup', (event) => {
        if (!start) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        start = null;
        if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
        this.suppressNextClick(signal);
        const direction = dx < 0 ? 1 : -1;
        this.go(this.index + direction * (this.isRtl ? -1 : 1));
      }, { signal });

      this.addEventListener('pointercancel', () => { start = null; }, { signal });
    }
  }

  customElements.define('zyma-slider', ZymaSlider);
})();
